// src/tabs/logements/LivretFaqEditor.jsx — Phase A.7.1b (ADR-120)
//
// Sous-onglet "FAQ" éditeur livret admin.
// CRUD livret_faqs : question/answer JSONB FR/EN + order_index.
//
// MVP : CRUD simple sans drag-drop (Phase A.7.2 si besoin).
// Reordering via flèches ↑↓ manuelles (UX simple, suffisant pour MVP).

import { useState, useCallback } from "react";
import { DS } from "../../lib/ui";
import { HelpCircle, Plus, Pencil, Trash2, Languages, AlertTriangle } from "lucide-react";
import { supabase } from "../../supabase";

async function getAuthHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

export default function LivretFaqEditor({ logement_id, faqs, onReload, lang }) {
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // {id?, question, answer}
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(
    async (entry) => {
      setSaving(true);
      setError(null);
      try {
        const headers = await getAuthHeaders();
        const isEdit = !!entry.id;
        const resp = await fetch(
          "/api/beds24-mapping?action=livret_faq_upsert",
          {
            method: isEdit ? "PATCH" : "POST",
            headers,
            body: JSON.stringify({
              action: "livret_faq_upsert",
              logement_id,
              ...entry,
            }),
          },
        );
        const json = await resp.json();
        if (!resp.ok)
          throw new Error(json.message || json.error || "save_failed");
        setEditing(null);
        await onReload?.();
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setSaving(false);
      }
    },
    [logement_id, onReload],
  );

  async function handleDelete(id) {
    if (!window.confirm("Supprimer cette FAQ définitivement ?")) return;
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        "/api/beds24-mapping?action=livret_faq_delete",
        {
          method: "POST",
          headers,
          body: JSON.stringify({ action: "livret_faq_delete", id }),
        },
      );
      const json = await resp.json();
      if (!resp.ok)
        throw new Error(json.message || json.error || "delete_failed");
      await onReload?.();
    } catch (e) {
      setError(e.message || String(e));
    }
  }

  const list = Array.isArray(faqs) ? faqs : [];

  return (
    <div
      style={{
        background: DS.card,
        borderRadius: DS.radiusCard,
        padding: 18,
        border: `0.5px solid ${DS.border}`,
        fontFamily: DS.fontBody,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          fontFamily: DS.fontDisplay,
          fontSize: 15,
          fontWeight: 600,
          color: DS.marineDark,
          marginBottom: 14,
        }}
      >
        <span style={{
          display: "inline-flex", width: 28, height: 28, borderRadius: 8,
          background: DS.catCielBg, color: DS.catCielIco, alignItems: "center", justifyContent: "center",
        }}>
          <HelpCircle size={16} strokeWidth={1.8} />
        </span>
        Foire aux questions
      </div>

      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            padding: 10,
            background: DS.dangerBg,
            border: `0.5px solid ${DS.danger}`,
            borderRadius: 8,
            color: DS.dangerText,
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          <AlertTriangle size={15} strokeWidth={1.8} style={{ flex: "0 0 auto" }} /> {error}
        </div>
      )}

      <button
        type="button"
        onClick={() =>
          setEditing({ question: { fr: "" }, answer: { fr: "" }, order_index: list.length })
        }
        className="fbz-press"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 14px",
          background: DS.corail,
          color: "#fff",
          border: "none",
          borderRadius: DS.radiusControl,
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          marginBottom: 14,
          fontFamily: DS.fontBody,
        }}
      >
        <Plus size={15} strokeWidth={2} /> Ajouter une FAQ
      </button>

      {list.length === 0 && !editing ? (
        <div
          style={{
            padding: 16,
            border: `1px dashed ${DS.border}`,
            borderRadius: 8,
            color: DS.textMuted,
            fontSize: 12,
            fontStyle: "italic",
            textAlign: "center",
          }}
        >
          Aucune FAQ pour l'instant.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {list.map((faq) => (
            <FaqRow
              key={faq.id}
              faq={faq}
              lang={lang}
              onEdit={() => setEditing(faq)}
              onDelete={() => handleDelete(faq.id)}
            />
          ))}
        </div>
      )}

      {editing && (
        <FaqInlineForm
          entry={editing}
          saving={saving}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function FaqRow({ faq, onEdit, onDelete, lang }) {
  // L1b — affichage liste dans la langue globale active (fallback FR).
  const qFr =
    faq?.question?.[lang || "fr"] ||
    faq?.question?.fr ||
    "(sans question FR)";
  return (
    <div
      className="fbz-rowhover"
      style={{
        padding: 12,
        border: `0.5px solid ${DS.border}`,
        borderRadius: 8,
        background: DS.card,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div style={{ fontSize: 13, color: DS.marineDark, flex: 1, marginRight: 12 }}>
        <strong>{qFr}</strong>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button
          type="button"
          onClick={onEdit}
          className="fbz-press"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "5px 10px",
            background: DS.card,
            color: DS.marine,
            border: `0.5px solid ${DS.borderHover}`,
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: DS.fontBody,
          }}
        >
          <Pencil size={13} strokeWidth={1.8} /> Éditer
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="fbz-press"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "5px 10px",
            background: DS.card,
            color: DS.danger,
            border: `0.5px solid ${DS.border}`,
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: DS.fontBody,
          }}
        >
          <Trash2 size={13} strokeWidth={1.8} /> Supprimer
        </button>
      </div>
    </div>
  );
}

function FaqInlineForm({ entry, saving, onCancel, onSave }) {
  const [qFr, setQFr] = useState(entry?.question?.fr || "");
  const [qEn, setQEn] = useState(entry?.question?.en || "");
  const [qEs, setQEs] = useState(entry?.question?.es || "");
  const [aFr, setAFr] = useState(entry?.answer?.fr || "");
  const [aEn, setAEn] = useState(entry?.answer?.en || "");
  const [aEs, setAEs] = useState(entry?.answer?.es || "");

  // Sprint A.9.2 PR-A92-9 (ADR-137) : traduction auto FAQ
  const [translating, setTranslating] = useState({ en: false, es: false });
  const [translateError, setTranslateError] = useState(null);

  const isValid = qFr.trim().length > 0 && aFr.trim().length > 0;

  async function handleTranslateFaq(targetLocale) {
    if (!qFr.trim() && !aFr.trim()) {
      setTranslateError("Renseigner question et réponse FR avant traduction.");
      return;
    }
    setTranslating((prev) => ({ ...prev, [targetLocale]: true }));
    setTranslateError(null);
    try {
      const headers = await getAuthHeaders();
      const callTranslate = async (sourceText, context) => {
        if (!sourceText.trim()) return null;
        const resp = await fetch(
          "/api/beds24-mapping?action=livret_translate",
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              action: "livret_translate",
              source_text: sourceText.trim(),
              target_locale: targetLocale,
              context,
            }),
          },
        );
        const json = await resp.json();
        if (!resp.ok || !json.ok) {
          throw new Error(json.message || json.error || "translate_failed");
        }
        return json;
      };

      const [qResult, aResult] = await Promise.all([
        callTranslate(qFr, "faq_question"),
        callTranslate(aFr, "faq_answer"),
      ]);

      const anyFallback =
        (qResult && qResult.fallback) || (aResult && aResult.fallback);
      if (anyFallback) {
        setTranslateError(
          `Traduction ${targetLocale.toUpperCase()} indisponible (Claude API). Réessayer ou saisir manuellement.`,
        );
      }

      if (qResult && !qResult.fallback && qResult.translated) {
        if (targetLocale === "en") setQEn(qResult.translated);
        else setQEs(qResult.translated);
      }
      if (aResult && !aResult.fallback && aResult.translated) {
        if (targetLocale === "en") setAEn(aResult.translated);
        else setAEs(aResult.translated);
      }
    } catch (e) {
      setTranslateError(
        `Erreur traduction ${targetLocale.toUpperCase()} : ${e.message || "indisponible"}`,
      );
    } finally {
      setTranslating((prev) => ({ ...prev, [targetLocale]: false }));
    }
  }

  function submit() {
    if (!isValid) return;
    onSave({
      ...(entry.id ? { id: entry.id } : {}),
      question: {
        fr: qFr.trim(),
        ...(qEn.trim() ? { en: qEn.trim() } : {}),
        ...(qEs.trim() ? { es: qEs.trim() } : {}),
      },
      answer: {
        fr: aFr.trim(),
        ...(aEn.trim() ? { en: aEn.trim() } : {}),
        ...(aEs.trim() ? { es: aEs.trim() } : {}),
      },
      order_index: typeof entry.order_index === "number" ? entry.order_index : 0,
    });
  }

  return (
    <div
      style={{
        marginTop: 14,
        padding: 12,
        background: DS.toolBg,
        borderRadius: 8,
        border: `0.5px solid ${DS.toolBorder}`,
      }}
    >
      {/* Sprint A.9.2 PR-A92-9 (ADR-137) : boutons traduction auto FAQ — action
          IA secondaire (icône Languages), jamais en corail. */}
      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => handleTranslateFaq("en")}
          disabled={
            !qFr.trim() || translating.en || translating.es || saving
          }
          className="fbz-press"
          style={faqTranslateBtnStyle(translating.en)}
          aria-label="Traduire FAQ FR vers EN"
        >
          <Languages size={13} strokeWidth={1.8} /> {translating.en ? "EN…" : "FR → EN"}
        </button>
        <button
          type="button"
          onClick={() => handleTranslateFaq("es")}
          disabled={
            !qFr.trim() || translating.es || translating.en || saving
          }
          className="fbz-press"
          style={faqTranslateBtnStyle(translating.es)}
          aria-label="Traduire FAQ FR vers ES"
        >
          <Languages size={13} strokeWidth={1.8} /> {translating.es ? "ES…" : "FR → ES"}
        </button>
      </div>
      {translateError && (
        <div
          style={{
            padding: 6,
            background: DS.dangerBg,
            border: `0.5px solid ${DS.danger}`,
            borderRadius: 6,
            color: DS.dangerText,
            fontSize: 11,
            marginBottom: 8,
          }}
        >
          {translateError}
        </div>
      )}
      <div style={{ display: "grid", gap: 8 }}>
        <input
          type="text"
          value={qFr}
          onChange={(e) => setQFr(e.target.value)}
          placeholder="Question (FR, obligatoire)"
          disabled={saving}
          style={inpStyleInline}
        />
        <input
          type="text"
          value={qEn}
          onChange={(e) => setQEn(e.target.value)}
          placeholder="Question (EN, optionnel)"
          disabled={saving}
          style={inpStyleInline}
        />
        <input
          type="text"
          value={qEs}
          onChange={(e) => setQEs(e.target.value)}
          placeholder="Question (ES, optionnel)"
          disabled={saving}
          style={inpStyleInline}
        />
        <textarea
          value={aFr}
          onChange={(e) => setAFr(e.target.value)}
          placeholder="Réponse (FR, obligatoire)"
          rows={3}
          disabled={saving}
          style={{ ...inpStyleInline, resize: "vertical" }}
        />
        <textarea
          value={aEn}
          onChange={(e) => setAEn(e.target.value)}
          placeholder="Réponse (EN, optionnel)"
          rows={3}
          disabled={saving}
          style={{ ...inpStyleInline, resize: "vertical" }}
        />
        <textarea
          value={aEs}
          onChange={(e) => setAEs(e.target.value)}
          placeholder="Réponse (ES, optionnel)"
          rows={3}
          disabled={saving}
          style={{ ...inpStyleInline, resize: "vertical" }}
        />
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="fbz-press"
          style={{
            padding: "6px 12px",
            background: DS.card,
            color: DS.textSecondary,
            border: `0.5px solid ${DS.borderHover}`,
            borderRadius: DS.radiusControl,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: DS.fontBody,
          }}
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={saving || !isValid}
          className="fbz-press"
          style={{
            padding: "6px 12px",
            background: !isValid || saving ? "#cbd5e1" : DS.corail,
            color: "#fff",
            border: "none",
            borderRadius: DS.radiusControl,
            fontSize: 12,
            fontWeight: 600,
            cursor: !isValid || saving ? "not-allowed" : "pointer",
            fontFamily: DS.fontBody,
          }}
        >
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
    </div>
  );
}

// Sprint A.9.2 PR-A92-9 (ADR-137) : bouton traduction auto inline FAQ
const faqTranslateBtnStyle = (loading) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "5px 10px",
  background: DS.card,
  color: loading ? DS.textMuted : DS.marine,
  border: `0.5px solid ${DS.borderHover}`,
  borderRadius: DS.radiusControl,
  fontSize: 11,
  fontWeight: 600,
  cursor: loading ? "wait" : "pointer",
  opacity: loading ? 0.7 : 1,
  fontFamily: DS.fontBody,
});

const inpStyleInline = {
  border: `0.5px solid ${DS.border}`,
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: 13,
  outline: "none",
  background: DS.card,
  fontFamily: DS.fontBody,
  width: "100%",
  boxSizing: "border-box",
};
